import { addMonths } from "date-fns";
import { IModelInput, IModelSectionInput, INestedModelInput, ISimulatorInput, ISimulatorModel } from "../inputs";
import { SimulationContext } from "../simulation-context";
import { applyAdditionDiscount } from "../financial-service/financial.service";
import { getMonthToReset } from "../utils";
import { IMonthlyPatientSplit } from "../patient-service/interfaces";
import { getPatientSplit } from "../patient-service/patient.service";
import {
	DiscountMeasuringPeriodOptionLabels,
	FreeOrDiscountedInitiationScopeOptionLabels,
	RegimenWeightBasedOption,
	VolumeBaseOptionLabels,
} from "apps/platform-client/client/src/models/simulator/options";
import { mergeIndications } from "./merge-output-indications";

const isPatientScopeSection = (section: IModelSectionInput) => {
	const isPatientLevel =
		section.volumeScope === VolumeBaseOptionLabels.PATIENT_LEVEL ||
		section.freeOrDiscountedScope === FreeOrDiscountedInitiationScopeOptionLabels.PATIENT_LEVEL;
	const nestedModelSection = section.nestedModel?.sections[0];
	const isNestedPatientLevel =
		nestedModelSection?.volumeScope === VolumeBaseOptionLabels.PATIENT_LEVEL ||
		nestedModelSection?.freeOrDiscountedScope === FreeOrDiscountedInitiationScopeOptionLabels.PATIENT_LEVEL;

	return isPatientLevel || isNestedPatientLevel;
};

const isWeightBasedSection = (section: IModelSectionInput, input: ISimulatorModel) => {
	const regimen = input.treatmentRegimen.find((regimen) => section.indications && regimen.indications?.includes(section.indications[0]));

	return regimen?.pack.name === RegimenWeightBasedOption.MG_KG_OF_PATIENT_WEIGHT;
};

export const GROUP_LABEL = "-group-";
export const simulateModel = (input: ISimulatorModel, simulationDuration: number) => {
	const simulationStartDate = new Date(input.simulationDuration.startDate);
	const simulationDurationSplit = [...Array(simulationDuration).keys()];
	const weightGroups = input.patientWeight;
	const isWeightBased = isWeightBasedSection(input.models[0].sections[0], input);
	const hasPatientScopeSection = input.models.some((m) => m.sections.some((section) => isPatientScopeSection(section)));
	const inputWithWeightSections = {
		...input,
		models: input.models.map((m) => ({
			...m,
			sections: m.sections.flatMap((section, index) => {
				return isWeightBased && hasPatientScopeSection ? increaseSectionCount(section, weightGroups.length) : section;
			}),
		})),
	};

	const simulationInput = mapSimulatorModelToInput(inputWithWeightSections);
	const context = new SimulationContext(simulationInput);
	const initialState = simulationInput.model.sections.reduce((result, section) => {
		result[section.id] = [] as Array<IMonthlyPatientSplit>;

		return result;
	}, {} as { [key: string]: Array<IMonthlyPatientSplit> });

	const finalState = simulationDurationSplit.reduce((result, month) => {
		const currentDate = addMonths(simulationStartDate, month);
		simulationInput.model.sections.forEach((section) => {
			const sectionValue = result[section.id];
			const isWeightBased = section.id.includes(GROUP_LABEL);
			const weightGroupId = isWeightBased ? section.id.split(GROUP_LABEL)[1] : null;
			const previousMonthSplit = sectionValue.length
				? sectionValue[sectionValue.length - 1]
				: ({ activePatients: [], currentMonth: new Date() } as IMonthlyPatientSplit);
			result[section.id] = [
				...sectionValue,
				getPatientSplit({
					currentDate,
					currentMonth: month,
					previousMonthSplit,
					sectionId: section.id,
					context,
					groupIndexShare: weightGroupId ? weightGroups[+weightGroupId - 1].share : undefined,
				}),
			];

			const { models } = context.getFinancialServiceInput(section.id);
			models.forEach((model) => {
				if (
					[DiscountMeasuringPeriodOptionLabels.CALENDAR_YEAR, DiscountMeasuringPeriodOptionLabels.FISCAL_YEAR].includes(
						model.discountMeasuringPeriod
					)
				) {
					const monthToReset = getMonthToReset(model.discountMeasuringPeriod, model.discountMeasuringCalendarMonth);

					if (currentDate.getMonth() === monthToReset) {
						context.resetVolumeBasedTotalAggregationValue(section.id);
					}
				}
			});
		});

		return result;
	}, initialState);

	// apply additional discount if needed
	const simulationContext = context.getSimulationContext();
	const finalResult = Object.entries(finalState).flatMap(([key, value]) => {
		const sectionUpdates = simulationContext.get(key);
		const indicationKey = simulationInput.model.sections.find((section) => section.id === key)!.indications;
		const simulationStartDate = context.getSimulationStartDate();

		if (sectionUpdates) {
			return [
				{
					indication: indicationKey,
					data: value.map((monthlySplit, index) => {
						const additionalDiscounts = sectionUpdates.additionalDiscounts.get(index) || [];
						const discountNotIncluded = sectionUpdates.discountNotIncluded.get(index) || [];

						return additionalDiscounts.length > 0
							? applyAdditionDiscount(monthlySplit, additionalDiscounts, simulationStartDate, discountNotIncluded)
							: monthlySplit;
					}),
				},
			];
		}

		return [{ indication: indicationKey, data: value }];
	});

	return mergeIndications(finalResult);
};

export const mapSimulatorModelToInput = (simulatorModel: ISimulatorModel): ISimulatorInput => {
	if (!simulatorModel.models.length) throw Error("Model is missing for simulation!");

	const models = [...simulatorModel.models];
	const parentModel = models.shift()!;

	return {
		...simulatorModel,
		model: models.reduce(combineModels, parentModel),
	};
};

const nestModel = (model: INestedModelInput, newModel: IModelInput): INestedModelInput => {
	return {
		...model,
		sections: model.sections.map((section) => ({
			...section,
			nestedModel: section.nestedModel ? nestModel(section.nestedModel as INestedModelInput, newModel) : newModel,
		})),
	};
};

export const combineModels = (finalModel: IModelInput, model: IModelInput): IModelInput => {
	const hasCommonIndication = (currentIndication: string[], comingIndications: string[]) => {
		return comingIndications.length === currentIndication.length && comingIndications.every((ind) => currentIndication.includes(ind));
	};

	const combinedSections = [...finalModel.sections];

	// multiple sections may have same indication when use weight groups.
	const matchingIndexes = combinedSections
		.map((section, index) => ({
			index,
			// section.indications is passed for older simulations that do not have indication in parallel models
			// sections[0] is used because all the models can have only one sections, except the indication model which can be selected only as first model, and will never be nested.
			isMatch: hasCommonIndication(section.indications, model.sections[0].indications || section.indications),
		}))
		.filter(({ isMatch }) => isMatch)
		.map(({ index }) => index);

	if (matchingIndexes.length === 0) {
		combinedSections.push(...model.sections);
	} else {
		matchingIndexes.forEach((index) => {
			const existingSection = combinedSections[index];
			combinedSections[index] = {
				...existingSection,
				nestedModel: existingSection.nestedModel
					? nestModel(existingSection.nestedModel, model)
					: { ...model, sections: model.sections },
			};
		});
	}

	return {
		...finalModel,
		sections: combinedSections,
	};
};

const increaseSectionCount = (section: IModelSectionInput, sectionLength: number) => {
	return Array.from({ length: sectionLength }, (s, i) => ({
		...section,
		id: `${section.id}${GROUP_LABEL}${i + 1}`,
	}));
};
